<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <Hero :background="contactBg">
      <div
        class="w-full max-w-[760px] ml-auto rounded-md shadow-xl border border-white/30 bg-white/30 backdrop-blur-md p-6">
        <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4 drop-shadow-lg">
          Fale conosco
        </h2>
        <div v-if="submitted"
          class="bg-white/70 backdrop-blur-md rounded-md border border-white/30 shadow p-6 text-center">
          <div class="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
            <svg class="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-800 mt-4">
            Mensagem enviada com sucesso!
          </h3>
          <p class="text-sm text-gray-600 mt-1 leading-relaxed">
            Recebemos seu contato e retornaremos em breve.
          </p>
          <button type="button" @click="submitted = false"
            class="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-kali-blue-600 text-white text-sm font-medium hover:bg-kali-blue-700 transition">
            Enviar outra mensagem
          </button>
        </div>
        <template v-else>
          <p v-if="formErrorSummary" class="text-sm text-red-600 mb-2">
            {{ formErrorSummary }}
          </p>
          <form @submit.prevent="submitForm" class="space-y-4" novalidate>
            <label for="name" class="sr-only">Nome completo</label>
            <input type="text" id="name" v-model="form.name" placeholder="Seu nome" autocomplete="name"
              class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              required />
            <p v-if="errors.name" class="text-sm text-red-600 mt-1">
              {{ errors.name }}
            </p>
            <label for="email" class="sr-only">E-mail</label>
            <input type="email" id="email" v-model="form.email" placeholder="Seu e-mail" autocomplete="email"
              class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              required />
            <p v-if="errors.email" class="text-sm text-red-600 mt-1">
              {{ errors.email }}
            </p>
            <label for="phone" class="sr-only">Telefone</label>
            <input type="tel" id="phone" v-model="form.phone" @input="onPhoneInput" inputmode="numeric"
              placeholder="Seu telefone" autocomplete="tel"
              class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              required />
            <p v-if="errors.phone" class="text-sm text-red-600 mt-1">
              {{ errors.phone }}
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="state" class="sr-only">Estado</label>
                <select id="state" v-model="form.state"
                  class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-600 focus:border-kali-blue-600 border border-gray-300 rounded-md"
                  :class="form.state ? 'text-gray-900' : 'text-gray-500'" required>
                  <option value="" disabled>Estado</option>
                  <option v-for="s in brazilStates" :key="s.code" :value="s.code">
                    {{ s.name }}
                  </option>
                </select>
                <p v-if="errors.state" class="text-sm text-red-600 mt-1">
                  {{ errors.state }}
                </p>
              </div>
              <div>
                <label for="city" class="sr-only">Cidade</label>
                <select id="city" v-model="form.city" :disabled="isLoadingCities || !form.state"
                  class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-600 focus:border-kali-blue-600 border border-gray-300 rounded-md disabled:bg-gray-100"
                  :class="form.city ? 'text-gray-900' : 'text-gray-500'" required>
                  <option value="" disabled>
                    {{ isLoadingCities ? "Carregando..." : "Selecione" }}
                  </option>
                  <option v-for="c in availableCities" :key="c" :value="c">
                    {{ c }}
                  </option>
                </select>
                <p v-if="errors.city" class="text-sm text-red-600 mt-1">
                  {{ errors.city }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-4 text-gray-700">
              <label class="inline-flex items-center gap-2">
                <input type="checkbox" v-model="form.isClient" class="h-4 w-4 text-kali-blue-500 focus:ring-kali-blue-500" />
                Já sou cliente
              </label>
              <label class="inline-flex items-center gap-2">
                <input type="checkbox" v-model="form.notClient" class="h-4 w-4 text-kali-blue-500 focus:ring-kali-blue-500" />
                Não sou cliente
              </label>
            </div>
            <p v-if="clientStatusError" class="text-sm text-red-600">
              {{ clientStatusError }}
            </p>
            <label for="message" class="sr-only">Mensagem</label>
            <textarea id="message" v-model="form.message" rows="4" placeholder="Mensagem"
              class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              required></textarea>
            <p v-if="errors.message" class="text-sm text-red-600 mt-1">
              {{ errors.message }}
            </p>
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox" v-model="form.consentimento"
                class="mt-0.5 h-4 w-4 shrink-0 text-kali-blue-500 focus:ring-kali-blue-500"
              />
              <span class="text-xs text-gray-700 leading-relaxed">
                Li e concordo com a
                <a href="/politica-de-privacidade" target="_blank" class="text-kali-blue-700 font-semibold hover:underline">Política de Privacidade</a>
                da Kali Sistemas para o tratamento dos meus dados neste contato. *
              </span>
            </label>
            <p v-if="errors.consentimento" class="text-sm text-red-600 mt-1">
              {{ errors.consentimento }}
            </p>
            <button type="submit"
              class="w-full inline-flex justify-center py-2.5 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-kali-blue-600 hover:bg-kali-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kali-blue-500">
              ENVIAR
            </button>
          </form>
        </template>
      </div>
    </Hero>

    <!-- Contact Section -->
    <div class="relative bg-kali-blue-50">
      <div class="relative max-w-7xl mx-auto lg:grid lg:grid-cols-5">
        <div class="py-16 px-4 sm:px-6 lg:col-span-2 lg:px-8 lg:py-24 xl:pr-12">
          <div class="max-w-lg mx-auto">
            <h2 class="text-2xl font-extrabold tracking-tight text-kali-blue-600 sm:text-3xl">
              Informações de Contato
            </h2>
            <p class="mt-3 text-lg leading-6 text-kali-blue-700">
              Escolha a melhor forma de entrar em contato conosco. Estamos
              prontos para atender você.
            </p>
            <dl class="mt-8 text-base text-kali-blue-700">
              <div class="mt-6">
                <dt class="sr-only">Endereço</dt>
                <dd class="flex">
                  <svg class="flex-shrink-0 h-6 w-6 text-kali-blue-500" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span class="ml-3">
                    Av. Ivo do Prado, 352<br />
                    Centro, Aracaju/SE<br />
                    CEP 49.010-050
                  </span>
                </dd>
              </div>
              <div class="mt-3">
                <dt class="sr-only">Telefone</dt>
                <dd class="flex">
                  <svg class="flex-shrink-0 h-6 w-6 text-kali-blue-500" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span class="ml-3"> (79) 99138-9415 </span>
                </dd>
              </div>
              <div class="mt-3">
                <dt class="sr-only">Email</dt>
                <dd class="flex">
                  <svg class="flex-shrink-0 h-6 w-6 text-kali-blue-500" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span class="ml-3"> contato@kalisistemas.com.br </span>
                </dd>
              </div>
            </dl>
            <p class="mt-6 text-base text-kali-blue-700">
              Horário de atendimento:<br />
              Segunda a Sexta, das 8h às 18h<br />
              Sábado, das 9h às 12h
            </p>
          </div>
        </div>
        <div class="py-16 px-4 sm:px-6 lg:col-span-3 lg:px-8 lg:py-24">
          <div class="h-96 md:h-[460px] rounded-md overflow-hidden shadow border border-gray-200">
            <iframe title="Mapa - Av. Ivo do Prado, 352, Aracaju/SE"
              src="https://www.google.com/maps?q=Av.%20Ivo%20do%20Prado%2C%20352%2C%20Aracaju%20-%20SE%2C%2049010-050&output=embed"
              class="w-full h-full" style="border: 0" allowfullscreen loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: "ContactPage" });
import { ref, watch } from 'vue'
import Hero from '@/components/contact/Hero.vue'
import contactBg from '@/assets/contact-bg.webp'
import api from '@/services/api'
import Swal from 'sweetalert2'
import { usePhoneMask } from '@/composables/usePhoneMask'
import { useUtm } from '@/composables/useUtm'
import { useSeo } from '@/composables/useSeo'
import { pushEvent } from '@/composables/useGtm'
import { useAbTest } from '@/composables/useAbTest'
import { PRIVACY_POLICY_VERSION } from '@/constants/legal'

const { variant: abVariant, testKey: abTestKey, initAbTest } = useAbTest()
if (typeof window !== 'undefined') initAbTest()

useSeo({
  title: "Fale Conosco | Kali Sistemas",
  description:
    "Entre em contato com a Kali Sistemas. Tire suas dúvidas, solicite uma demonstração ou fale com nosso time comercial.",
  image: contactBg,
})

const { formatPhone } = usePhoneMask()
const { getUtmParams } = useUtm()

const submitted = ref(false);

const brazilStates = [
  { code: "AC", name: "Acre" },
  { code: "AL", name: "Alagoas" },
  { code: "AM", name: "Amazonas" },
  { code: "AP", name: "Amapá" },
  { code: "BA", name: "Bahia" },
  { code: "CE", name: "Ceará" },
  { code: "DF", name: "Distrito Federal" },
  { code: "ES", name: "Espírito Santo" },
  { code: "GO", name: "Goiás" },
  { code: "MA", name: "Maranhão" },
  { code: "MT", name: "Mato Grosso" },
  { code: "MS", name: "Mato Grosso do Sul" },
  { code: "MG", name: "Minas Gerais" },
  { code: "PA", name: "Pará" },
  { code: "PB", name: "Paraíba" },
  { code: "PR", name: "Paraná" },
  { code: "PE", name: "Pernambuco" },
  { code: "PI", name: "Piauí" },
  { code: "RJ", name: "Rio de Janeiro" },
  { code: "RN", name: "Rio Grande do Norte" },
  { code: "RO", name: "Rondônia" },
  { code: "RS", name: "Rio Grande do Sul" },
  { code: "RR", name: "Roraima" },
  { code: "SC", name: "Santa Catarina" },
  { code: "SE", name: "Sergipe" },
  { code: "SP", name: "São Paulo" },
  { code: "TO", name: "Tocantins" },
];

const form = ref({
  name: "",
  email: "",
  phone: "",
  state: "",
  city: "",
  isClient: false,
  notClient: false,
  message: "",
  consentimento: false,
});

const errors = ref({
  name: "",
  email: "",
  phone: "",
  state: "",
  city: "",
  message: "",
  consentimento: "",
});
const formErrorSummary = ref("");

const ufToIbgeId = {
  AC: 12,
  AL: 27,
  AM: 13,
  AP: 16,
  BA: 29,
  CE: 23,
  DF: 53,
  ES: 32,
  GO: 52,
  MA: 21,
  MT: 51,
  MS: 50,
  MG: 31,
  PA: 15,
  PB: 25,
  PR: 41,
  PE: 26,
  PI: 22,
  RJ: 33,
  RN: 24,
  RO: 11,
  RS: 43,
  RR: 14,
  SC: 42,
  SE: 28,
  SP: 35,
  TO: 17,
};

const availableCities = ref([]);
const isLoadingCities = ref(false);
const cityError = ref("");
const citiesCache = ref({});
const clientStatusError = ref("");

watch(
  () => form.value.state,
  async (uf) => {
    form.value.city = "";
    availableCities.value = [];
    cityError.value = "";
    if (!uf) return;

    if (citiesCache.value[uf]) {
      availableCities.value = citiesCache.value[uf];
      return;
    }

    const id = ufToIbgeId[uf];
    if (!id) return;

    try {
      isLoadingCities.value = true;
      const res = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${id}/municipios?orderBy=nome`,
      );
      if (!res.ok) throw new Error("Erro ao carregar municípios");
      const data = await res.json();
      const list = Array.isArray(data) ? data.map((m) => m.nome) : [];
      citiesCache.value[uf] = list;
      availableCities.value = list;
    } catch (e) {
      console.error(e);
      cityError.value = "Não foi possível carregar os municípios";
      availableCities.value = [];
    } finally {
      isLoadingCities.value = false;
    }
  },
);

// Exclusividade entre os checkboxes
watch(
  () => form.value.isClient,
  (v) => {
    if (v) {
      form.value.notClient = false;
      clientStatusError.value = "";
    }
  },
);
watch(
  () => form.value.notClient,
  (v) => {
    if (v) {
      form.value.isClient = false;
      clientStatusError.value = "";
    }
  },
);

// Limpa mensagens de erro conforme usuário digita/seleciona
watch(
  form,
  (v) => {
    if (v.name) errors.value.name = "";
    if (v.email) errors.value.email = "";
    if (v.phone) errors.value.phone = "";
    if (v.state) errors.value.state = "";
    if (v.city) errors.value.city = "";
    if (v.message) errors.value.message = "";
    if (v.isClient || v.notClient) clientStatusError.value = "";
    if (v.consentimento) errors.value.consentimento = "";
  },
  { deep: true },
);

const onPhoneInput = (e) => {
  form.value.phone = formatPhone(e.target.value)
}

const validateForm = () => {
  errors.value = {
    name: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    message: "",
    consentimento: "",
  };
  formErrorSummary.value = "";
  let valid = true;
  const { name, email, phone, state, city, message, isClient, notClient, consentimento } =
    form.value;
  if (!name.trim()) {
    errors.value.name = "Preencha seu nome completo";
    valid = false;
  }
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  if (!email.trim() || !emailOk) {
    errors.value.email = "Informe um e-mail válido";
    valid = false;
  }
  if (!phone.trim()) {
    errors.value.phone = "Informe seu telefone";
    valid = false;
  }
  if (!state) {
    errors.value.state = "Selecione o estado";
    valid = false;
  }
  if (state && !isLoadingCities.value && !city) {
    errors.value.city = "Selecione a cidade";
    valid = false;
  }
  if (!message.trim()) {
    errors.value.message = "Escreva sua mensagem";
    valid = false;
  }
  if (!isClient && !notClient) {
    clientStatusError.value = "Selecione se você é cliente ou não";
    valid = false;
  }
  if (!consentimento) {
    errors.value.consentimento = "É necessário aceitar a Política de Privacidade para continuar";
    valid = false;
  }
  if (!valid) {
    formErrorSummary.value = "Preencha os campos obrigatórios.";
  }
  return valid;
};

const submitForm = async () => {
  if (!validateForm()) return;

  const { utm_source, utm_medium, utm_campaign } = getUtmParams({ utm_campaign: 'contact' })

  const payload = {
    name: form.value.name,
    email: form.value.email,
    phone: form.value.phone.replace(/\D/g, "").slice(0, 11) || form.value.phone,
    message: form.value.message,
    source: "form",
    page_url: window.location.href,
    utm_source,
    utm_medium,
    utm_campaign,
    // Novos campos alinhados à tabela de Leads do CRM
    company: form.value.company || null,
    pipeline: "novo",
    project_type: null,
    reason: null,
    opportunity_type: null,
    seller: null,
    city: form.value.city || null,
    uf: form.value.state || null,
    municipio_uf:
      form.value.city && form.value.state
        ? `${form.value.city}/${form.value.state}`
        : null,
    lgpd: {
      accepted: form.value.consentimento,
      policy_version: PRIVACY_POLICY_VERSION,
      text: 'Li e concordo com a Política de Privacidade da Kali Sistemas para o tratamento dos meus dados neste contato.',
    },
    payload: {
      source_cta: 'contact-form',
      ab_test: abTestKey.value ? { key: abTestKey.value, variant: abVariant.value } : undefined,
      state: form.value.state,
      city: form.value.city,
      isClient: form.value.isClient,
      notClient: form.value.notClient,
    },
  };

  try {
    // Modal de loading não bloqueante (sem await)
    Swal.fire({
      title: "Enviando...",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await api.post("/public/lead", payload);

    Swal.close();
    submitted.value = true;
    pushEvent('lead_contact', { source_page: window.location.pathname })
    // Reset do formulário
    form.value = {
      name: "",
      email: "",
      phone: "",
      state: "",
      city: "",
      isClient: false,
      notClient: false,
      message: "",
      consentimento: false,
    };
    clientStatusError.value = "";
    errors.value = {
      name: "",
      email: "",
      phone: "",
      state: "",
      city: "",
      message: "",
      consentimento: "",
    };
    formErrorSummary.value = "";
  } catch (err) {
    const resp = err?.response;
    if (resp?.status === 422 && resp.data?.errors) {
      const e = resp.data.errors;
      errors.value.name = e.name?.[0] || "";
      errors.value.email = e.email?.[0] || "";
      errors.value.phone = e.phone?.[0] || "";
      errors.value.message = e.message?.[0] || "";
      errors.value.state = e.state?.[0] || "";
      errors.value.city = e.city?.[0] || "";
      formErrorSummary.value =
        "Corrija os campos destacados e tente novamente.";
      Swal.close();
    } else {
      const status = resp?.status;
      const code = err?.code;
      const isTimeout = code === "ECONNABORTED";
      const isNetwork =
        !resp && err?.message?.toLowerCase().includes("network");

      let title = "Falha ao enviar";
      let text = "Tente novamente mais tarde.";
      if (isTimeout) {
        title = "Tempo de resposta excedido";
        text = "O servidor demorou para responder. Tente novamente.";
      } else if (isNetwork) {
        title = "Não foi possível conectar";
        text = "Verifique sua conexão com a internet e tente novamente.";
      } else if (status >= 500) {
        title = "Instabilidade no servidor";
        text = "Estamos com instabilidade. Tente novamente em alguns minutos.";
      } else if (status === 404) {
        title = "Serviço indisponível";
        text = "Endpoint não encontrado no servidor. Tente novamente.";
      }

      formErrorSummary.value = "Falha ao enviar. Tente novamente mais tarde.";
      console.error("Erro ao enviar lead:", {
        message: err?.message,
        code,
        status,
        url: `${api.defaults.baseURL}/public/lead`,
        details: resp?.data,
      });
      Swal.close();
      Swal.fire({
        title,
        text,
        icon: "error",
        iconColor: "#ef4444",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      });
    }
  }
};
</script>
